import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import { ArrowIcon } from '../../../assets/icons/index';
import { AlertDialogWrapper } from '../../../components/alert-dialog/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import Button from '../../../components/button/index';
import RootLoader from '../../../components/root-loader/index';
import { QueryKey } from '../../../constants/index';
import { useSelected } from '../../../hooks/company/index';
import {
  ScenarioHooks,
  useInvalidateQueries,
  useModalState,
  useReactForm,
  useScreenSize,
} from '../../../hooks/index';
import { scenarioCapTableGeneratedStore } from '../../../storage/local/index';
import { temporaryScenarioStakeholdersStore } from '../../../storage/local/index';
import { PageRoute } from '../../../types/page.types';
import { ScenarioType, ScenarioTypeTitle } from '../../../types/scenario-modeling.types';
import { Snapshot, SnapshotListItem } from '../../../types/snapshot.types';
import { useScenarioCapTableStore } from '../cap-table.store';
import { CapTableWarningModal, HurdleWarningModal } from '../modals/index';
import { ScenarioFundraisingCapTable } from '../scenario-cap-table/fundraising/index';
import { ScenarioLiquidityCapTable } from '../scenario-cap-table/liquidity/index';
import { useScenarioStore } from '../scenario-modeling.store';
import { CapTableSelect } from './cap-table-select';
import { ConversionSummary } from './conversion-summary';
import { fundraisingFormSchema } from './fundraising-validation';
import { liquidityFormSchema } from './liquidity-validation';
import { MobileDetails } from './mobile-details/index';
import { FundraisingCreation } from './scenario-creation/fundraising/index';
import { LiquidityCreation } from './scenario-creation/liquidity/index';
import { useGrantStore } from './scenario-creation/scenario-dropdown/grant.store';
import { useSafeStore } from './scenario-creation/scenario-dropdown/safe.store';

const ScenarioDetails: FC = () => {
  const { width } = useScreenSize();
  const isMobile = width < 992;
  const { id } = useParams();
  const navigate = useNavigate();
  const scenarioId = id || '';

  const { selectedCompanyId, selectedCompany, isLoading: isLoadingCompany } = useSelected();
  const { scenario, isLoading } = ScenarioHooks.useScenario({ scenarioId });
  const { data: capTableData } = ScenarioHooks.useCapTableShareClasses({ scenarioId });
  const { calcCapTable, isPending: isCapTablePending } = ScenarioHooks.useCalcCapTable();
  const { invalidateQuery } = useInvalidateQueries(QueryKey.GET_SCENARIO, QueryKey.GET_SCENARIOS);
  const { invalidateQuery: invalidateScenarioDerivedQueries } = useInvalidateQueries(
    QueryKey.CAP_TABLE_STAKEHOLDERS,
    QueryKey.CAP_TABLE_STAKEHOLDER_TYPES,
    QueryKey.CAP_TABLE_STAKEHOLDER_BY_ALL,
    QueryKey.CAP_TABLE_SHARE_CLASSES,
    QueryKey.CAP_TABLE_SUMMARIZE,
    QueryKey.GET_CONVERSION_SUMMARY,
  );
  const { scenarioInvestments } = ScenarioHooks.useScenarioInvestments({ scenarioId });
  const { scenarioCustomSafes } = ScenarioHooks.useScenarioCustomSafes({ scenarioId });
  const { scenarioGrants } = ScenarioHooks.useScenarioGrants({ scenarioId });
  const { classConversions } = ScenarioHooks.useScenarioClassConversions({ scenarioId });
  const { scenarioSars } = ScenarioHooks.useScenarioSars({ scenarioId });
  const { scenarioVestingTasks } = ScenarioHooks.useScenarioVestingTasks({ scenarioId });

  const { scenarioSafes } = ScenarioHooks.useScenarioSafes({ scenarioId });
  const { scenarioPools } = ScenarioHooks.useScenarioPools({ scenarioId });

  const {
    conversionSummary,
    isLoading: isLoadingConversionSummary,
    isRefused: isConversionRefused,
    isSuccess: isLoadedCapTableSummarize,
  } = ScenarioHooks.useConversionSummary({
    enabled: Boolean(scenario),
    scenarioId,
  });

  const setSelectedSafesCount = useSafeStore(({ setSelectedItemsCount }) => setSelectedItemsCount);
  const safeStates = useSafeStore(({ scenarioStates }) => scenarioStates);

  const setSelectedGrantsCount = useGrantStore(
    ({ setSelectedItemsCount }) => setSelectedItemsCount,
  );
  const scenarioStates = useGrantStore(({ scenarioStates }) => scenarioStates);

  const grantsStore = scenarioStates[scenarioId] ?? null;
  const safesStore = safeStates[scenarioId] ?? null;

  const [selectedVariant, setSelected] = useState<ReactNode | string>(
    scenario?.valuation?.name || <AppFormattedMessage id="LATEST_CAP_TABLE" />,
  );
  const [snapshot, setSnapshot] = useState<Snapshot | SnapshotListItem | undefined>();

  const scheduled = useScenarioCapTableStore(({ scheduled }) => scheduled);
  const registerUpdate = useScenarioCapTableStore(({ registerUpdate }) => registerUpdate);

  const capTableGenerated = scenarioCapTableGeneratedStore((state) => state.value);
  const setCapTableGenerated = scenarioCapTableGeneratedStore((state) => state.set);
  const isUpdateRequired = Boolean(scheduled[scenarioId]);
  const hasGeneratedCapTable = Boolean(scenario?.isGeneratedFirstCapTable || capTableGenerated);

  const { control, setValue, reset } = useReactForm({
    schema: scenario?.type === ScenarioType.LIQUIDITY ? liquidityFormSchema : fundraisingFormSchema,
  });

  const setSharePrice = useScenarioStore(({ setSharePrice }) => setSharePrice);
  const sharePrice = useScenarioStore(({ sharePrice }) => sharePrice);
  const setScenarioId = useScenarioStore(({ setScenarioId }) => setScenarioId);

  const { handleCloseModal, handleOpenModal, isOpen } = useModalState();

  const {
    toggler: warningModalToggler,
    handleCloseModal: handleCloseWarningModal,
    handleOpenModal: handleOpenWarningModal,
    isOpen: isOpenWarningModal,
  } = useModalState();

  const {
    toggler: hurdleWarningModalToggler,
    handleCloseModal: handleCloseHurdleWarningModal,
    handleOpenModal: handleOpenHurdleWarningModal,
    isOpen: isOpenHurdleWarningModal,
  } = useModalState();

  const lastResetKeyRef = useRef<null | string>(null);
  const capTableWarningShownRef = useRef(false);

  const handleOpenCapTable = () => {
    if (scheduled[scenarioId] ?? !capTableData?.length) {
      return calcCapTable(
        { scenarioId },
        {
          onSuccess: () => {
            registerUpdate(scenarioId);
            setCapTableGenerated(true);
            handleOpenModal();
            invalidateQuery();
          },
        },
      );
    }

    invalidateScenarioDerivedQueries();
    handleOpenModal();
    setCapTableGenerated(true);
  };

  useEffect(() => {
    setScenarioId(scenarioId);
  }, [scenarioId, setScenarioId]);

  useEffect(() => {
    if (conversionSummary && conversionSummary?.sharePrice) {
      if (conversionSummary.sharePrice !== sharePrice) {
        setSharePrice(conversionSummary.sharePrice);
      }
    }
  }, [conversionSummary, setSharePrice, sharePrice]);

  useEffect(() => {
    if (!scenario) return;
    const nextResetPayload = {
      accelerateVesting: scenario.accelerateVesting,
      valuation: {
        date: scenario.closingDate ? scenario.closingDate : undefined,
        preMoneyValuation: scenario.preMoneyValuation,
        type: scenario.type,
      },
      customSafes: {
        safeItems:
          scenarioCustomSafes && scenarioCustomSafes?.length > 0
            ? scenarioCustomSafes?.map(
                ({
                  cap,
                  capType,
                  discount,
                  floor,
                  investment,
                  shareClass,
                  stakeholder,
                  id,
                  fakeStakeholderName,
                }) => ({
                  id,
                  cap,
                  capType: capType ?? null,
                  discount: discount * 100,
                  floor,
                  investment,
                  shareClass,
                  stakeholder: stakeholder || { fullName: fakeStakeholderName, id: 'new' },
                }),
              )
            : undefined,
      },
      grants: {
        grantItems:
          scenarioGrants && scenarioGrants?.length > 0
            ? scenarioGrants?.map(({ id, vestingTask, issuedShares }) => ({
                id,
                vestingTaskId: vestingTask?.id,
                issuedShares,
              }))
            : undefined,
      },
      classConversion: {
        classConversionItems:
          classConversions && classConversions?.length > 0
            ? classConversions?.map(
                ({ id, classConversionTask, conversionRatio, preferredShares, ...data }) => ({
                  id,
                  classConversionTaskId: classConversionTask?.id,
                  conversion: conversionRatio,
                  convertedShares: preferredShares,
                  ...data,
                }),
              )
            : undefined,
      },
      // sars: {
      //   exercise:
      //     scenarioSars &&
      //     scenarioVestingSars &&
      //     scenarioSars?.length === scenarioVestingSars?.length &&
      //     !scenarioSars?.find((s) => s.issuedShares === 0),
      //   payout:
      //     scenarioSars &&
      //     scenarioVestingSars &&
      //     scenarioSars?.length === scenarioVestingSars?.length &&
      //     !scenarioSars?.find((s) => s.payout === 0),
      //   sarItems:
      //     scenarioSars && scenarioSars?.length > 0
      //       ? scenarioSars?.map(({ id, vestingTask, issuedShares, payout }) => ({
      //           id,
      //           grantId: vestingTask?.id,
      //           issuedShares,
      //           payout,
      //         }))
      //       : undefined,
      // },
      investments: {
        investmentItems:
          scenarioInvestments && scenarioInvestments?.length > 0
            ? scenarioInvestments?.map(
                ({ investment, shareClass, stakeholder, id, fakeStakeholderName }) => ({
                  id,
                  investment,
                  shareClass,
                  stakeholder: stakeholder || { fullName: fakeStakeholderName, id: 'new' },
                }),
              )
            : undefined,
      },
      pools: {
        poolItems:
          scenarioPools && scenarioPools?.length > 0
            ? scenarioPools
                ?.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
                ?.map(({ dilutedShares, name, shareClass, type, id }) => ({
                  id,
                  dilutedShares,
                  name,
                  shareClass,
                  type,
                }))
            : undefined,
      },
      safes: {
        safeItems:
          scenarioSafes && scenarioSafes.length > 0
            ? scenarioSafes.reduce(
                (acc, { safe, shareClass, id }) => {
                  if (
                    !safe?.id ||
                    safe.isConvertedToShares ||
                    !shareClass?.id ||
                    !shareClass?.name
                  ) {
                    return acc;
                  }

                  acc[safe.id] = {
                    id,
                    safeId: safe.id,
                    shareClass: {
                      id: shareClass.id,
                      name: shareClass.name,
                    },
                  };
                  return acc;
                },
                {} as Record<
                  string,
                  { id: string; safeId: string; shareClass: { id: string; name: string } }
                >,
              )
            : undefined,
      },
    };

    let nextResetKey: null | string = null;
    try {
      nextResetKey = JSON.stringify(nextResetPayload);
    } catch (error) {
      nextResetKey = null;
    }

    if (nextResetKey && lastResetKeyRef.current === nextResetKey) return;
    if (nextResetKey) lastResetKeyRef.current = nextResetKey;

    reset(nextResetPayload);

    const tempSafeStakeholders = (scenarioCustomSafes ?? [])
      ?.map(({ fakeStakeholderName }) => fakeStakeholderName?.toLowerCase())
      .filter(Boolean) as string[];
    const tempInvestmentStakeholders = (scenarioInvestments ?? [])
      ?.map(({ fakeStakeholderName }) => fakeStakeholderName?.toLowerCase())
      .filter(Boolean) as string[];

    const updated = [...tempSafeStakeholders, ...tempInvestmentStakeholders];
    temporaryScenarioStakeholdersStore.set({
      [scenarioId]: updated,
    });
  }, [
    scenario,
    reset,
    scenarioCustomSafes,
    scenarioGrants,
    scenarioInvestments,
    scenarioPools,
    scenarioSafes,
    scenarioSars,
    scenarioId,
    scenarioVestingTasks,
    classConversions,
  ]);

  useEffect(() => {
    if (isLoading || isLoadingConversionSummary) return;
    if (!isOpenWarningModal && !isOpen && (!capTableGenerated || scenario?.isHasBeenChanged)) {
      const timer = setTimeout(() => {
        if (conversionSummary?.isHurdleTriggered) {
          handleOpenHurdleWarningModal(true);
        }
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [
    capTableGenerated,
    conversionSummary?.isHurdleTriggered,
    handleOpenHurdleWarningModal,
    isLoading,
    isLoadingConversionSummary,
    isOpen,
    isOpenWarningModal,
    scenario?.isHasBeenChanged,
  ]);

  useEffect(() => {
    if (!scenario || isMobile || !hasGeneratedCapTable || !isUpdateRequired) {
      capTableWarningShownRef.current = false;
      return;
    }

    if (capTableWarningShownRef.current || isOpenHurdleWarningModal || isOpen || isOpenWarningModal)
      return;

    handleOpenWarningModal();
    capTableWarningShownRef.current = true;
  }, [
    handleOpenWarningModal,
    hasGeneratedCapTable,
    isMobile,
    isOpen,
    isOpenHurdleWarningModal,
    isOpenWarningModal,
    isUpdateRequired,
    scenario,
  ]);

  useEffect(() => {
    if (scenarioGrants && grantsStore?.selectedItemsCount !== scenarioGrants?.length) {
      setSelectedGrantsCount(scenarioId, scenarioGrants.length);
    }
  }, [grantsStore?.selectedItemsCount, scenarioGrants, scenarioId, setSelectedGrantsCount]);

  useEffect(() => {
    if (scenarioSafes && safesStore?.selectedItemsCount !== scenarioSafes?.length) {
      setSelectedSafesCount(scenarioId, scenarioSafes.length);
    }
  }, [safesStore?.selectedItemsCount, scenarioId, scenarioSafes, setSelectedSafesCount]);

  return (
    <>
      {(isLoading || isLoadingCompany || !scenario) && <RootLoader />}
      {scenario && (
        <>
          <AlertDialogWrapper
            control={{
              onOpenChange: hurdleWarningModalToggler,
              open: isOpenHurdleWarningModal,
            }}
          >
            <HurdleWarningModal onClose={handleCloseHurdleWarningModal} />
          </AlertDialogWrapper>
          <AlertDialogWrapper
            control={{ onOpenChange: warningModalToggler, open: isOpenWarningModal }}
          >
            <CapTableWarningModal onClose={handleCloseWarningModal} />
          </AlertDialogWrapper>

          {scenario.type === ScenarioType.FUNDRAISING ? (
            <ScenarioFundraisingCapTable
              companyId={selectedCompanyId}
              handleClose={handleCloseModal}
              isLoading={isCapTablePending}
              isOpen={isOpen ? isLoadedCapTableSummarize : false}
              scenarioId={scenarioId}
            />
          ) : (
            <ScenarioLiquidityCapTable
              companyId={selectedCompanyId}
              handleClose={handleCloseModal}
              isLoading={isCapTablePending}
              isOpen={isOpen ? isLoadedCapTableSummarize : false}
              scenarioId={scenarioId}
            />
          )}
          {isMobile ? (
            <MobileDetails scenario={scenario} />
          ) : (
            <div className="flex h-full w-full flex-col gap-6 overflow-x-hidden lg:gap-4">
              <div className="flex items-center justify-between rounded-md bg-white p-4 shadow-sm">
                <div className="flex items-center gap-2">
                  <Button
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-[1px] border-[#F2F2F2]"
                    onClick={() => navigate(PageRoute.SCENARIO_MODELING)}
                    styleType="NONE"
                  >
                    <ArrowIcon />
                  </Button>
                  <span className="text-base font-semibold text-gray-700">{scenario.name}</span>
                </div>
                <div className="flex gap-16">
                  <div
                    className={twMerge(
                      'rounded-[4px] border-[1px] px-3 py-2 text-sm font-[450]',
                      scenario.type === ScenarioType.FUNDRAISING
                        ? 'border-forest-50 bg-forest-25 text-forest-600'
                        : 'border-sunbeam-50 bg-sunbeam-50 text-[#C56A08]',
                    )}
                  >
                    <AppFormattedMessage id={ScenarioTypeTitle[scenario.type]} />
                  </div>
                  {scenario.type === ScenarioType.FUNDRAISING ? (
                    <CapTableSelect
                      companyId={selectedCompanyId}
                      scenario={scenario}
                      selectedVariant={selectedVariant}
                      setSelected={setSelected}
                      setSnapshot={setSnapshot}
                    />
                  ) : (
                    <div className="rounded-[4px] border-[1px] border-gray-50 bg-gray-50 px-3 py-2 text-sm font-[450] text-gray-700">
                      <AppFormattedMessage id="LATEST_CAP_TABLE" />
                    </div>
                  )}
                </div>
              </div>
              <div className="flex h-full w-full flex-col-reverse gap-4 lg:flex-row">
                {scenario.type === ScenarioType.FUNDRAISING ? (
                  <FundraisingCreation
                    companyId={selectedCompanyId || ''}
                    control={control}
                    scenarioId={scenarioId}
                    scenarioType={ScenarioType.FUNDRAISING}
                    setValue={setValue}
                    snapshot={snapshot}
                  />
                ) : (
                  <LiquidityCreation
                    companyId={selectedCompanyId || ''}
                    control={control}
                    scenarioId={scenarioId}
                    scenarioType={ScenarioType.LIQUIDITY}
                    setValue={setValue}
                    snapshot={snapshot}
                  />
                )}
                <ConversionSummary
                  control={control}
                  conversionSummary={conversionSummary}
                  currencySign={selectedCompany?.currency?.symbol}
                  isCapTableLoading={isCapTablePending}
                  isCapTablePending={isCapTablePending}
                  isConversionRefused={isConversionRefused}
                  isLoadingSummary={isLoadingConversionSummary}
                  onOpenCapTable={handleOpenCapTable}
                  scenario={scenario}
                />
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default ScenarioDetails;
